import type {
  MachineAttachmentItemDto,
  MachineQuotationItemDto,
  QuotationPriceOnlyDto
} from '@api/machines'
import { formatPriceAndCurrency } from '@common/utils'
import {
  MolsonAccordion,
  MolsonBox,
  MolsonDivider,
  MolsonIcon,
  MolsonTypography
} from '@components/reusable'
import type { TypographyPropsVariantOverrides } from '@mui/material'
import type { Variant } from '@mui/material/styles/createTypography'
import type { OverridableStringUnion } from '@mui/types'
import { type molsonTheme } from '@theme'
import { molsonStyled, molsonUseTheme } from '@theme/customMuiExports'

export const CustomMolsonAccordion = molsonStyled(MolsonAccordion)(
  ({ theme, type }: { theme?: typeof molsonTheme; type?: string }) => ({
    '& .MuiAccordionSummary-content > div h4:nth-of-type(2)': {
      color: type === 'tradeIn' && theme?.palette.primary.error
    },
    '& div[role="region"] > div': {
      paddingBottom: '10px !important'
    },

    '&::after': {
      height: '0 !important'
    }
  })
)

export const PriceBreakdownItem = ({
  archived = false,
  machine,
  name,
  onRemove,
  price,
  removable = false,
  showDivider,
  type = 'small',
  quoteNewPrices
}: {
  archived?: boolean
  machine?: MachineQuotationItemDto
  name: null | string | undefined
  onRemove?: () => void
  price?: string
  removable?: boolean
  showDivider?: boolean
  type?: 'large' | 'medium' | 'small'
  quoteNewPrices?: QuotationPriceOnlyDto
}) => {
  const theme = molsonUseTheme()

  let nameVariant: OverridableStringUnion<
    'inherit' | Variant,
    TypographyPropsVariantOverrides
  > = 'body2'

  switch (type) {
    case 'large':
    case 'medium':
      nameVariant = 'body1'
      break
    case 'small':
      nameVariant = 'body2'
      break
    default:
      nameVariant = 'body2'
  }

  const itemSx = () => {
    let color = ''
    let textDecoration = ''
    if (archived) {
      color = theme.palette.primary.error ?? ''
      textDecoration = 'line-through'
    } else if (!archived && removable) {
      color = theme.palette.primary.light
      textDecoration = 'none'
    } else {
      color = 'currentColor'
    }
    return { color, fontWeight: 400, textDecoration }
  }

  const hasAttachmentsWithinMachine =
    (machine?.machineAttachments ?? []).length > 0

  const totalAttachmentsWithinMachine = machine?.machineAttachments?.reduce(
    (total, attachment) =>
      total + (attachment.quantity ?? 1) * (machine?.quantity ?? 1),
    0
  )

  const displayAttachmentQuantity = (machine?.quantity ?? 0) > 1

  return (
    <MolsonBox display="flex" flexDirection="column">
      <MolsonBox
        alignItems="center"
        display="flex"
        justifyContent="space-between"
      >
        <MolsonTypography sx={itemSx()} variant={nameVariant}>
          {name}
        </MolsonTypography>
        {price && (
          <MolsonTypography
            alignItems="center"
            display="flex"
            fontSize={14}
            ml={2}
          >
            {!archived && price}
            {removable && (
              <MolsonIcon
                sx={{
                  marginLeft: 1
                }}
                cursor="pointer"
                dataTestId="breakdown_item_remove_icon"
                onClick={onRemove}
                variant="CLOSE_X"
              />
            )}
          </MolsonTypography>
        )}
      </MolsonBox>

      {showDivider && !hasAttachmentsWithinMachine && (
        <MolsonDivider
          sx={{ borderColor: theme.palette.secondary[200], mb: 2, mt: 2 }}
        />
      )}
      {hasAttachmentsWithinMachine && (
        <CustomMolsonAccordion
          primaryTextSx={{
            color: theme.palette.secondary[500],
            fontSize: '12px',
            fontWeight: 600
          }}
          sx={{
            '.MuiAccordionDetails-root': {
              padding: '8px 0px 24px 0px'
            },
            '.MuiAccordionSummary-content': {
              margin: '8px 0 0 !important'
            }
          }}
          primaryText={`+ Attachments (${totalAttachmentsWithinMachine})`}
          primaryTextVariant="body1"
          size="sm"
          summarySx={{ padding: '0 0 0 8px' }}
        >
          <MolsonBox display="flex" flexDirection="column" gap={1}>
            {machine?.machineAttachments?.map(
              (machineAttachment: MachineAttachmentItemDto) => {
                const newPrice = quoteNewPrices?.machineAttachmentItems?.find(
                  newPriceAttachment =>
                    newPriceAttachment.quotationItemId ===
                    machineAttachment.quotationItemId
                )?.price

                return (
                  <MolsonBox
                    key={
                      machineAttachment.quotationItemId ?? machineAttachment.id
                    }
                    display="flex"
                    justifyContent="space-between"
                  >
                    <MolsonTypography
                      color={theme.palette.secondary[500]}
                      fontSize="12px"
                      fontWeight={400}
                      pl={2.5}
                      variant={nameVariant}
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                    >
                      <span>{machineAttachment.modelName}</span>
                      {displayAttachmentQuantity && (
                        <span>
                          (
                          {(machineAttachment?.quantity ?? 0) *
                            (machine?.quantity ?? 0)}
                          )
                        </span>
                      )}
                    </MolsonTypography>
                    <MolsonTypography
                      color={theme.palette.secondary[500]}
                      fontSize="12px"
                      variant={nameVariant}
                    >
                      {formatPriceAndCurrency(
                        (newPrice ?? machineAttachment.price ?? 0) *
                          (machineAttachment.quantity ?? 0)
                      )}
                    </MolsonTypography>
                  </MolsonBox>
                )
              }
            )}
          </MolsonBox>
        </CustomMolsonAccordion>
      )}

      {showDivider && hasAttachmentsWithinMachine && (
        <MolsonDivider
          sx={{ borderColor: theme.palette.secondary[200], mb: 2, mt: 2 }}
        />
      )}
    </MolsonBox>
  )
}
